/**
 * Game Status State Machine
 *
 * Enforces legal transitions between game lifecycle states.
 * No code path may publish a game without going through the
 * approved transitions. All transitions are auditable.
 */

export enum GameStatus {
  DRAFT = 'DRAFT',
  BUILDING = 'BUILDING',
  TESTING = 'TESTING',
  APPROVED = 'APPROVED',
  PUBLISHED = 'PUBLISHED',
  PAUSED = 'PAUSED',
  KILLED = 'KILLED',
}

/**
 * Legal state transitions.
 * Key = current status, Value = set of allowed next statuses.
 */
export const ALLOWED_TRANSITIONS: ReadonlyMap<GameStatus, ReadonlySet<GameStatus>> = new Map<
  GameStatus,
  ReadonlySet<GameStatus>
>([
  [GameStatus.DRAFT, new Set([GameStatus.BUILDING, GameStatus.KILLED])],
  [GameStatus.BUILDING, new Set([GameStatus.TESTING, GameStatus.DRAFT, GameStatus.KILLED])],
  [
    GameStatus.TESTING,
    new Set([GameStatus.APPROVED, GameStatus.BUILDING, GameStatus.DRAFT, GameStatus.KILLED]),
  ],
  [GameStatus.APPROVED, new Set([GameStatus.PUBLISHED, GameStatus.TESTING, GameStatus.KILLED])],
  [GameStatus.PUBLISHED, new Set([GameStatus.PAUSED, GameStatus.KILLED])],
  [GameStatus.PAUSED, new Set([GameStatus.PUBLISHED, GameStatus.KILLED])],
  [GameStatus.KILLED, new Set([])],
]);

export interface TransitionResult {
  allowed: boolean;
  from: GameStatus;
  to: GameStatus;
  reason?: string;
}

/**
 * Check if a transition from one status to another is allowed.
 */
export function canTransition(from: GameStatus, to: GameStatus): TransitionResult {
  if (from === to) {
    return { allowed: true, from, to, reason: 'no-op transition' };
  }

  const allowed = ALLOWED_TRANSITIONS.get(from);
  if (!allowed) {
    return { allowed: false, from, to, reason: `unknown source status: ${from}` };
  }

  if (allowed.has(to)) {
    return { allowed: true, from, to };
  }

  return {
    allowed: false,
    from,
    to,
    reason: `transition ${from} -> ${to} is not allowed. Allowed targets: ${[...allowed].join(', ') || '(none)'}`,
  };
}

/**
 * Attempt a transition. Throws if not allowed.
 * Use this in mutation paths to enforce the state machine.
 */
export function transition(from: GameStatus, to: GameStatus): GameStatus {
  const result = canTransition(from, to);
  if (!result.allowed) {
    throw new IllegalStatusTransitionError(result);
  }
  return to;
}

export class IllegalStatusTransitionError extends Error {
  public readonly from: GameStatus;
  public readonly to: GameStatus;

  constructor(result: TransitionResult) {
    super(`Illegal game status transition: ${result.from} -> ${result.to}. ${result.reason ?? ''}`);
    this.name = 'IllegalStatusTransitionError';
    this.from = result.from;
    this.to = result.to;
  }
}

/**
 * Get all possible next statuses for a given status.
 */
export function getNextStatuses(status: GameStatus): GameStatus[] {
  const allowed = ALLOWED_TRANSITIONS.get(status);
  return allowed ? [...allowed] : [];
}

/**
 * Check if a game is in a terminal state (no further transitions possible).
 */
export function isTerminal(status: GameStatus): boolean {
  const allowed = ALLOWED_TRANSITIONS.get(status);
  return !allowed || allowed.size === 0;
}

/**
 * Check if a game is publicly visible (published or paused).
 */
export function isPubliclyVisible(status: GameStatus): boolean {
  return status === GameStatus.PUBLISHED || status === GameStatus.PAUSED;
}
